// @ts-nocheck
import qs from 'query-string';
import _ from 'lodash';
import { AxiosResponse } from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'libs/Axios';

import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  RequestCubeInsightlView,
  ResponseCubeInisghtView,
  ColumnMapItem,
} from 'types/api/Tenant/AROCube/CubeInisghtViewTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  createTreeData,
  getSortedKTreeId,
  mapKTreeMemberWithId,
  mapKTreeMemberWithParent,
  sortTreeData,
} from 'utils/Object';
import { KTreeTableItem } from 'types/api/Tenant/AROKMS/KTreeDataTypes';
import { Data } from 'types/api/Tenant/AROCube/CubeDataEntryTypes';
import { useSearchParams } from 'react-router-dom';

export const CUBE_INSIGHT_VIEW_QUERY_KEY = {
  CUBE_INSIGHT_VIEW_DATA: 'CUBE_INSIGHT_VIEW_DATA',
  CUBE_INSIGHT_VIEW_DROPDOWN: 'CUBE_INSIGHT_VIEW_DROPDOWN',
  CUBE_INSIGHT_VIEW_ATTRIBUTE_DROPDOWN: 'CUBE_INSIGHT_VIEW_ATTRIBUTE_DROPDOWN',
};

export function useGetCubeInsightViewData(payload: RequestCubeInsightlView, enabled = true) {
  const {
    filterMemberValue,
    insightViewId,
    cubeId,
    selectedXOptionType,
    selectedYOptionType,
    showMemberOnly,
    proxyMemberAccessId,
    measureChartCubeAttributeId,
    refChartId,
    refChartMemberIds,
    refChartAccumulationType,
    currentTimePeriod,
    hierarchyFilterId,
  } = payload;

  const [searchParams] = useSearchParams();
  const myViewId = searchParams.get('myViewId') || undefined;
  return useQuery<AxiosResponse<ResponseCubeInisghtView>, AxiosDefaultErrorEntity>(
    [CUBE_INSIGHT_VIEW_QUERY_KEY.CUBE_INSIGHT_VIEW_DATA, payload],
    () =>
      axios
        .get(
          `/api/v1/tenant/insight-view/display/${insightViewId}/${filterMemberValue}?${qs.stringify({
            cubeId,
            xDimensionType: selectedXOptionType,
            yDimensionType: selectedYOptionType,
            showMemberOnly,
            proxyMemberAccessId,
            myViewId,
            measureChartCubeAttributeId,
            refChartId,
            refChartMemberIds,
            accumulationRule: refChartAccumulationType,
            currentTimePeriod,
            hierarchyFilterId,
          })}`,
        )
        .then((res: AxiosResponse<ResponseCubeInisghtView>) => {
          if (selectedYOptionType === 'MAIN_MEMBER' && !showMemberOnly) {
            const allMembersAndParents = res.data.memberColumns.map((item: KTreeTableItem) => item.member);
            const mapAttributeWithParent = mapKTreeMemberWithParent(res.data.memberColumns);
            const mapKTreeMemberWithID = mapKTreeMemberWithId(res.data.memberColumns);
            const sortedKTreeIds = getSortedKTreeId(allMembersAndParents, mapAttributeWithParent, mapKTreeMemberWithID);
            const columnMap = _.cloneDeep(res.data.columnMap);
            const unorderData = _.cloneDeep(res.data.data);
            const orderedData: Data = {};

            let orderedMap: Map<String, ColumnMapItem> = new Map();
            sortedKTreeIds.forEach((key) => {
              // @ts-ignore
              if (columnMap[key]) {
                // @ts-ignore
                orderedMap.set(key, columnMap[key]);
              }
              if (unorderData[key]) {
                orderedData[key] = unorderData[key];
              }
            });

            const modifedResponse: AxiosResponse<ResponseCubeInisghtView> = {
              ...res,
              data: {
                ...res.data,
                data: orderedData,
              },
            };
            return modifedResponse;
          }

          return res;
        }),
    {
      enabled: !!insightViewId && !!filterMemberValue && !!cubeId && enabled,
    },
  );
}

export function useGetCubeInsightViewDropdown(cubeId: string) {
  return useQuery<AxiosResponse<AutoCompleteItem[]>, AxiosDefaultErrorEntity>(
    [CUBE_INSIGHT_VIEW_QUERY_KEY.CUBE_INSIGHT_VIEW_DROPDOWN, cubeId],
    () => axios.get(`/api/v1/tenant/insight-view/${cubeId}`).then((res) => res),
    {
      enabled: !!cubeId,
    },
  );
}

export function useGetInsightViewCubeAttributesDropdown(cubeId?: string | null, memberId?: string | null) {
  return useQuery<AxiosResponse<AutoCompleteItem[]>, AxiosDefaultErrorEntity>(
    [CUBE_INSIGHT_VIEW_QUERY_KEY.CUBE_INSIGHT_VIEW_ATTRIBUTE_DROPDOWN, cubeId, memberId],
    () =>
      axios
        .get(
          `/api/v1/tenant/insight-view/cube-attributes-dropdown/${cubeId}?${qs.stringify({
            memberId,
          })}`,
        )
        .then((res) => res),
    {
      enabled: !!cubeId,
    },
  );
}

/**
 * Chained prefetch: fetches insight view definition + filter member dropdown,
 * then prefetches the main insight view data with default parameters.
 */
export function usePrefetchInsightViewFullData() {
  const queryClient = useQueryClient();

  return (
    insightViewId: string,
    cubeId: string,
    myViewId?: string,
    defaultFilterMember?: string,
    proxyMemberId?: string,
    defaultXDimension?: string,
    defaultYDimension?: string,
  ) => {
    const finalXDimension = defaultXDimension || 'MEASUREMENT_MEMBER';
    const finalYDimension = defaultYDimension || 'INSIGHT_COLUMNS_MEMBER';
    // The page goes through: initial (MEASUREMENT_MEMBER) → final (defaultXDimension)
    const initialXDimension = 'MEASUREMENT_MEMBER';
    const initialYDimension = 'INSIGHT_COLUMNS_MEMBER';

    // 1. Prefetch insight view definition
    queryClient.prefetchQuery(
      ['INSIGHT_VIEW_DEFINITION_DETAILS', insightViewId],
      () => axios.get(`/api/v1/insight-view-definition/detail/${insightViewId}`).then((res) => res),
      { staleTime: 100000 },
    );

    // Helper: prefetch filter member dropdown for given dimension types
    const prefetchFilterMemberDropdown = (xDim?: string, yDim?: string) => {
      queryClient.prefetchQuery(
        [
          'CUBE_DIMENSIONAL_VIEW_FILTER_MEMBER',
          cubeId,
          xDim,
          yDim,
          'MAIN_MEMBER',
          'CUBE_INSIGHT_VIEW',
          insightViewId,
          proxyMemberId,
        ],
        () =>
          axios
            .get(
              `/api/v1/tenant/cube-view/dropdown/member/${cubeId}?${qs.stringify({
                xDimensionType: xDim,
                yDimensionType: yDim,
                filterType: 'MAIN_MEMBER',
                dimensionCategory: 'CUBE_INSIGHT_VIEW',
                insightViewId,
                proxyMemberAccessId: proxyMemberId,
                myViewId,
              })}`,
            )
            .then((res) => {
              if (res.data.length > 0) {
                const treeData = createTreeData(null, res.data);
                return sortTreeData(treeData);
              }
              return null;
            }),
        { staleTime: 100000 },
      );
    };

    // 2. Prefetch filter member dropdown for all intermediate states
    prefetchFilterMemberDropdown(undefined, undefined); // initial panel state
    prefetchFilterMemberDropdown(initialXDimension, initialYDimension); // after initialValues effect
    if (finalXDimension !== initialXDimension) {
      prefetchFilterMemberDropdown(finalXDimension, finalYDimension); // after defaultXDimension effect
    }

    // Helper: prefetch insight data for a given dimension + member + timePeriod combination
    const buildPayload = (
      member: string,
      xDim: string,
      yDim: string,
      currentTimePeriod?: string,
    ): RequestCubeInsightlView => ({
      insightViewId,
      filterMemberValue: member,
      selectedXOptionType: xDim,
      selectedYOptionType: yDim,
      showMemberOnly: false,
      selectedXOptionId: null,
      selectedYOptionId: null,
      cubeId,
      proxyMemberAccessId: proxyMemberId,
      measureChartCubeAttributeId: undefined,
      refChartId: undefined,
      refChartMemberIds: undefined,
      refChartAccumulationType: undefined,
      currentTimePeriod,
    });

    const fetchInsightData = (member: string, xDim: string, yDim: string, currentTimePeriod?: string) =>
      axios
        .get(
          `/api/v1/tenant/insight-view/display/${insightViewId}/${member}?${qs.stringify({
            cubeId,
            xDimensionType: xDim,
            yDimensionType: yDim,
            showMemberOnly: false,
            proxyMemberAccessId: proxyMemberId,
            myViewId,
            currentTimePeriod,
          })}`,
        )
        .then((res) => res);

    // 3. Prefetch insight data for all dimension × member × timePeriod combinations
    const filterMembers = new Set<string>();
    if (proxyMemberId) filterMembers.add(proxyMemberId);
    if (defaultFilterMember) filterMembers.add(defaultFilterMember);

    // Dimension states the page will cycle through
    const dimensionStates = [{ x: initialXDimension, y: initialYDimension }];
    if (finalXDimension !== initialXDimension) {
      dimensionStates.push({ x: finalXDimension, y: finalYDimension });
    }

    dimensionStates.forEach(({ x, y }) => {
      filterMembers.forEach((member) => {
        const initialPayload = buildPayload(member, x, y, undefined);
        queryClient
          .fetchQuery(
            [CUBE_INSIGHT_VIEW_QUERY_KEY.CUBE_INSIGHT_VIEW_DATA, initialPayload],
            () => fetchInsightData(member, x, y, undefined),
            { staleTime: 100000 },
          )
          .then((res) => {
            const selectedTimePeriod = res.data?.selectedOffsetTimePeriod?.value?.toString();
            if (selectedTimePeriod) {
              const timePeriodPayload = buildPayload(member, x, y, selectedTimePeriod);
              queryClient.prefetchQuery(
                [CUBE_INSIGHT_VIEW_QUERY_KEY.CUBE_INSIGHT_VIEW_DATA, timePeriodPayload],
                () => fetchInsightData(member, x, y, selectedTimePeriod),
                { staleTime: 100000 },
              );
            }
          });
      });
    });
  };
}
